import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import addDynamicRoutes, { processMenusForSidebar } from '@/utils/addRoutes';
import { useMenuStore } from '@/store/menuStore'; // 导入Pinia store
import { home } from '@/network';
import { clearAuthCache } from '@/utils/authCache';
const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    meta: { check: false, title: '建研国检智能检测系统' },
    component: () => import('../views/login/Login.vue')
  },
  {
    path: '/app-package-download',
    name: 'appPackageDownload',
    meta: { check: false, title: '客户端下载' },
    component: () => import('../views/KaiFa/AppPackageDownload/index.vue')
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/views/layout/layout.vue'),
    meta: { check: true },
    redirect: '/home',
    children: [
      {
        path: '/home',
        name: 'home',
        component: () => import('../views/shouYe/index.vue'),
        meta: { check: true, title: '首页' }
      },
      {
        path: '/参数设置/检测项目定义/打印列表',
        name: 'tableDefinitionPrintList',
        component: () => import('../views/ParameterSettings/tableEditing/printList.vue'),
        meta: { check: true, title: '打印列表', noCache: true }
      },
      {
        path: '/原始数据管理/经营项目数据分析/打印列表',
        name: 'printList',
        component: () => import('../views/RawDataManagement/AnalysisOfBusiness/printList.vue'),
        meta: { check: true, title: '原始记录打印', noCache: true }
      },
      {
        path: '/原始数据管理/经营项目数据分析/原始数据汇总',
        name: 'dataSummary',
        component: () => import('../views/RawDataManagement/AnalysisOfBusiness/summary.vue'),
        meta: { check: true, title: '原始数据汇总', noCache: true }
      },
      {
        path: '/原始数据管理/原始检测数据列表/检测数据详情',
        name: 'detectionDetails',
        component: () => import('../views/RawDataManagement/OriginalDetectionList/details.vue'),
        meta: { check: true, title: '原始记录详情' }
      },
      {
        path: '/房屋建筑定期体检/体检项目定义/体检记录详情',
        name: 'houseCheckRecordDetails',
        component: () => import('../views/HomeInspection/HouseCheckPlanDefinition/recordDetails.vue'),
        meta: { check: true, title: '体检记录详情', noCache: true }
      },
      {
        path: '/开发专用/App版本管理',
        name: 'appVersionAdmin',
        component: () => import('../views/KaiFa/AppVersionAdmin/index.vue'),
        meta: { check: true, title: 'App版本管理' }
      },
      {
        path: '/开发专用/系统监控',
        name: 'systemMonitor',
        component: () => import('../views/KaiFa/SystemMonitor/index.vue'),
        meta: { check: true, title: '系统监控' }
      }
    ],
  },
  // 404页面路由
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/error/NotFound.vue'),
    meta: { check: false, title: '页面未找到' }
  }
]

const DEFAULT_TITLE = '智能现场检测系统';

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

let verifyTokenPromise: Promise<boolean> | null = null;
let lastSuccessfulVerificationAt = 0;
const LOGIN_RECHECK_INTERVAL = 60 * 1000;

const getStoredToken = () => {
  const sessionToken = sessionStorage.getItem('wuyetoken');
  if (sessionToken) return sessionToken;

  const localToken = localStorage.getItem('wuyetoken');
  if (localToken) {
    sessionStorage.setItem('wuyetoken', localToken);
  }
  return localToken || '';
};

const clearLoginState = () => {
  verifyTokenPromise = null;
  lastSuccessfulVerificationAt = 0;
  clearAuthCache();
  try {
    useMenuStore().clearMenus();
  } catch (error) {
    console.error('清空菜单状态失败:', error);
  }
};

const verifyLoginToken = async (token: string) => {
  if (!token) return false;
  if (verifyTokenPromise) return verifyTokenPromise;

  verifyTokenPromise = home.GetInfoByToken(
    { token },
    { suppressBusinessErrorMessage: true }
  )
    .then((res: any) => {
      const uid = res?.response?.uID;
      if (!uid) {
        throw new Error('登录校验失败');
      }
      lastSuccessfulVerificationAt = Date.now();
      return true;
    })
    .catch((error) => {
      console.error('登录校验失败:', error);
      clearLoginState();
      return false;
    })
    .finally(() => {
      verifyTokenPromise = null;
    });

  return verifyTokenPromise;
};

const revalidateVisibleSession = async () => {
  if (document.visibilityState !== 'visible') return;
  if (Date.now() - lastSuccessfulVerificationAt < LOGIN_RECHECK_INTERVAL) return;

  const currentRoute = router.currentRoute.value;
  const requiresLogin = currentRoute.matched.some(record => record.meta.check === true);
  if (!requiresLogin) return;

  const token = getStoredToken();
  const isVerified = token ? await verifyLoginToken(token) : false;
  if (!isVerified && router.currentRoute.value.name !== 'login') {
    await router.replace({ name: 'login' });
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', () => {
    void revalidateVisibleSession();
  });
  document.addEventListener('visibilitychange', () => {
    void revalidateVisibleSession();
  });
}
// 处理中文路径编码的函数
function handleChinesePathDecoding(path: string): string {
  if (path.includes('%')) {
    try {
      const decodedPath = decodeURIComponent(path);
      console.log(`检测到编码的中文路径，尝试解码: ${path} -> ${decodedPath}`);
      return decodedPath;
    } catch (e) {
      console.error('路径解码失败:', e);
    }
  }
  return path;
}


router.beforeEach(async (to, from, next) => {
  const token = getStoredToken();
  
  console.log(`路由守卫: ${from.path} -> ${to.path}`);
  console.log(`路由匹配数量: ${to.matched.length}`, to.matched.map(r => r.path));
  
  // 处理根路径重定向
  if (to.path === '/') {
    console.log("根路径重定向到首页");
    next('/home');
    return;
  }
    // 处理中文路径编码问题 - 当浏览器刷新时路径可能被编码为UTF-8字符串
    if (to.path.includes('%')) {
      const decodedPath = handleChinesePathDecoding(to.path);
      if (decodedPath !== to.path) {
        // 检查解码后的路径是否匹配现有路由
        const resolvedRoute = router.resolve(decodedPath);
        if (resolvedRoute.matched.length > 0 && !resolvedRoute.matched.some(route => route.name === 'NotFound')) {
          console.log("找到解码后的路由，重定向到解码路径");
          // 保留原有的 query 参数
          next({ path: decodedPath, query: to.query });
          return;
        }
      }
    }
  // 未登录用户访问受保护路由
  if (!token && to.meta.check) {
    console.log("未登录，重定向到登录页");
    next({ name: 'login' });
    return;
  }

  if (token && to.meta.check) {
    const isVerified = await verifyLoginToken(token);
    if (!isVerified) {
      next({ name: 'login' });
      return;
    }
  }
  
  // 已登录用户访问登录页
  if (token && to.name === 'login') {
    const isVerified = await verifyLoginToken(token);
    if (isVerified) {
      console.log("已登录，重定向到首页");
      next('/home');
    } else {
      next();
    }
    return;
  }
  
  // 正常导航
  next();
});

router.afterEach((to) => {
  const title = typeof to.meta?.title === 'string' && to.meta.title.trim().length > 0
    ? to.meta.title
    : DEFAULT_TITLE;
  document.title = title;
});

export default router
